import apiClient from "./apiClient";

const BASE = "/api/Invoice";

const toDate = (d) => (d ? `${d}T00:00:00` : null);
const toNum = (v) => Number(v) || 0;

// API returns invoiceID (no `id`) — alias it so list/edit/delete code using `.id` works.
const withId = (inv) => ({ ...inv, id: inv.invoiceID });

export const getInvoices = async () => {
  const res = await apiClient.get(`${BASE}/all`);
  return { ...res, data: (res.data || []).map(withId) };
};
export const getInvoice = async (id) => {
  const res = await apiClient.get(`${BASE}/${id}`);
  return { ...res, data: withId(res.data) };
};

// Maps frontend model to API schema — shared by create and update.
// API InvoiceProcedureLine uses: procedureID, qty, rate, discount, amount
// API InvoiceProductLine uses:   productID, itemName, qty, rate, discount, amount
// API InvoicePayment uses:       paymentDate, paymentMode, amount, transactionRef
const toInvoicePayload = (data) => ({
  invoiceDate: toDate(data.invoiceDate),
  patientID: toNum(data.patientId || data.patientID),
  doctorID: toNum(data.doctorId || data.doctorID),
  appointmentID: toNum(data.appointmentId || data.appointmentID) || null,
  subtotal: toNum(data.subtotal || data.totalAmount),
  overallDiscount: toNum(data.discount || data.overallDiscount),
  taxAmount: toNum(data.taxAmount),
  netTotal: toNum(data.netTotal || data.totalAmount),
  paidAmount: toNum(data.paidAmount),
  balanceAmount: toNum(data.balanceAmount),
  invoiceStatus: data.status || data.invoiceStatus || "Unpaid",
  remarks: data.remarks || "",
  procedureLines: (data.services || data.procedureLines || [])
    .filter((s) => s.procedureId || s.procedureID)
    .map((s) => ({
      procedureID: toNum(s.procedureId || s.procedureID),
      qty: toNum(s.qty || s.quantity || 1),
      rate: toNum(s.rate),
      discount: toNum(s.discount),
      amount: toNum(s.amount),
    })),
  productLines: (data.products || data.productLines || [])
    .filter((p) => p.productId || p.productID)
    .map((p) => ({
      productID: toNum(p.productId || p.productID),
      qty: toNum(p.qty || p.quantity || 1),
      rate: toNum(p.rate),
      discount: toNum(p.discount),
      amount: toNum(p.amount),
    })),
  payments: (data.payments || [])
    .filter((p) => toNum(p.amount) > 0)
    .map((p) => ({
      paymentDate: toDate(p.paymentDate || data.invoiceDate),
      paymentMode: p.paymentMode || p.paymentType || "Cash",
      amount: toNum(p.amount),
      transactionRef: p.transactionRef || p.referenceNo || "",
    })),
});

export const createInvoice = (data) => apiClient.post(`${BASE}/generate`, toInvoicePayload(data));

// NOTE: PUT /api/Invoice/{id} is not yet implemented on the backend — this call is
// wired up ahead of that endpoint being built. Until it exists, this will 404/fail.
// See backend change request: full invoice update (header + procedure/product lines +
// full payments list, replacing the invoice's existing data).
export const updateInvoice = async (id, data) => {
  const res = await apiClient.put(`${BASE}/${id}`, { invoiceID: toNum(id), ...toInvoicePayload(data) });
  return { ...res, data: withId(res.data) };
};

export const deleteInvoice = () => Promise.reject(new Error("Delete not supported by API"));

// Add payment to existing invoice
export const addInvoicePayment = (data) =>
  apiClient.post(`${BASE}/add-payment`, {
    invoiceID: toNum(data.invoiceID),
    paymentDate: toDate(data.paymentDate),
    paymentMode: data.paymentMode || data.paymentType || "Cash",
    amount: toNum(data.amount || data.paymentAmount),
    transactionRef: data.transactionRef || data.referenceNo || "",
  });

// Pre-fill from appointment — not in API, return empty
export const getInvoiceFromAppointment = () => Promise.resolve({ data: null });

const dateOnly = (iso) => (iso ? iso.split("T")[0] : "");
const inRange = (d, from, to) => (!from || d >= from) && (!to || d <= to);

// Flattens every payment recorded against `invoices` whose paymentDate falls within
// [from, to] into one row per payment. There's no server-side "payments in a date
// range" endpoint, so this fetches full detail (which carries the payments[] array)
// for each candidate invoice and filters client-side — used by Payment Report, Daily
// Report and Bill-wise Receipt's recent-receipts listing.
// `candidates` is bounded to invoices dated in-range OR still outstanding, since only
// an outstanding invoice can receive a payment dated after its own invoice date.
export const getPaymentsInRange = async (invoices, from, to) => {
  const candidates = invoices.filter((inv) => inRange(dateOnly(inv.invoiceDate), from, to) || Number(inv.balanceAmount || 0) > 0);
  const details = await Promise.all(candidates.map((inv) => getInvoice(inv.id).then((r) => r.data).catch(() => null)));

  const out = [];
  details.filter(Boolean).forEach((inv) => {
    (inv.payments || []).forEach((p, i) => {
      const d = dateOnly(p.paymentDate);
      if (!inRange(d, from, to)) return;
      out.push({
        key: `${inv.invoiceID ?? inv.id}-${i}`,
        paymentDate: p.paymentDate,
        invoiceNumber: inv.invoiceNumber || inv.invoiceID || inv.id,
        invoiceId: inv.invoiceID ?? inv.id,
        patientId: inv.patientID ?? inv.patientId,
        patientName: inv.patientName || "—",
        paymentMode: p.paymentMode || "Cash",
        referenceNo: p.transactionRef || p.referenceNo || "—",
        amount: Number(p.amount || 0),
      });
    });
  });
  out.sort((a, b) => (b.paymentDate || "").localeCompare(a.paymentDate || ""));
  return out;
};
